if (!customElements.get('free-panel-board')) {
  class FreePanelBoard extends HTMLElement {
    constructor() {
      super();
      this.stage = this;
      this.panels = Array.from(this.querySelectorAll('[data-free-panel-item]'));
      this.zones = Array.from(this.querySelectorAll('[data-dock-zone]'));
      this.status = this.querySelector('[data-free-panel-status]');
      this.configNode = this.querySelector('script[type="application/json"]');
      this.stateKey = this.dataset.stateKey;
      this.dropIndicator = document.createElement('div');
      this.dropIndicator.className = 'free-panel__drop-indicator';
      this.dropIndicator.innerHTML = '<span class="free-panel__drop-indicator-label">吸附位置</span>';
      this.pointerSupport = 'PointerEvent' in window;
      this.dragState = null;
      this.lastTouchTime = 0;

      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseUp = this.onMouseUp.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
    }

    connectedCallback() {
      if (this.dataset.bound === 'true') {
        return;
      }

      this.dataset.bound = 'true';
      this.appendChild(this.dropIndicator);
      this.settings = this.readSettings();
      this.restoreState();
      if (this.settings.enableDrag !== false) {
        this.bindPanels();
      } else {
        this.classList.add('free-panel__stage--no-drag');
      }
      requestAnimationFrame(() => {
        this.relayout();
        this.renderStatus();
        this.panels.forEach((panel) => this.updateCoordReadout(panel));
      });
      window.addEventListener('resize', this.onWindowResize);
    }

    disconnectedCallback() {
      this.removeGlobalListeners();
      window.removeEventListener('resize', this.onWindowResize);
    }

    readSettings() {
      try {
        return JSON.parse(this.configNode.textContent);
      } catch (error) {
        return {
          gap: 16,
          desktopColumns: 2,
          mobileColumns: 1,
          defaultWidth: 320,
          defaultHeight: 280,
          minWidth: 220,
          minHeight: 180,
          mobileMinWidth: 180,
          mobileMinHeight: 150,
          dockZoneSize: 88,
          dockThreshold: 0.18,
          stageMinHeight: 620,
        };
      }
    }

    restoreState() {
      const saved = this.loadStoredState();

      this.panels.forEach((panel, index) => {
        const panelId = panel.dataset.panelId;
        const state = saved[panelId] || {};
        const rawWidth = (panel.dataset.baseWidth || '').trim();
        const rawHeight = (panel.dataset.baseHeight || '').trim();
        const isAutoWidth = rawWidth === 'auto' || rawWidth === '';
        const isAutoHeight = rawHeight === 'auto' || rawHeight === '';
        const configuredWidth = isAutoWidth
          ? (this.settings.defaultWidth || 320)
          : (Number(rawWidth) || this.settings.defaultWidth || 320);
        const configuredHeight = isAutoHeight
          ? this.autoHeightForPanel(panel, configuredWidth)
          : (Number(rawHeight) || this.settings.defaultHeight || 280);
        const userResizedWidth = !isAutoWidth && Boolean(state.userResizedWidth);
        const userResizedHeight = !isAutoHeight && Boolean(state.userResizedHeight);
        const baseWidth = userResizedWidth ? (Number(state.baseWidth) || Number(state.width) || configuredWidth) : configuredWidth;
        const baseHeight = userResizedHeight ? (Number(state.baseHeight) || Number(state.height) || configuredHeight) : configuredHeight;
        const resolvedHeight = Math.max(baseHeight, this.panelMinimumHeight(panel));
        panel.dataset.dock = state.dock || 'float';
        panel.dataset.order = String(state.order == null ? index : state.order);
        panel.dataset.dockOrder = String(state.dockOrder == null ? index : state.dockOrder);
        panel.dataset.userResizedWidth = userResizedWidth ? 'true' : 'false';
        panel.dataset.userResizedHeight = userResizedHeight ? 'true' : 'false';

        const schemaXStr = (panel.dataset.schemaX || '').trim();
        const schemaYStr = (panel.dataset.schemaY || '').trim();
        const schemaXPct = parseFloat(schemaXStr);
        const schemaYPct = parseFloat(schemaYStr);
        const hasSchemaPos = !isNaN(schemaXPct) && schemaXStr !== '' &&
                             !isNaN(schemaYPct) && schemaYStr !== '';
        const lastXPct = state.lastSchemaXPct != null ? parseFloat(state.lastSchemaXPct) : NaN;
        const lastYPct = state.lastSchemaYPct != null ? parseFloat(state.lastSchemaYPct) : NaN;
        const schemaChanged = hasSchemaPos && (
          isNaN(lastXPct) || isNaN(lastYPct) ||
          Math.abs(lastXPct - schemaXPct) > 0.01 ||
          Math.abs(lastYPct - schemaYPct) > 0.01
        );
        const sw = this.clientWidth || 1;

        if (hasSchemaPos && (schemaChanged || !state.placed)) {
          // Schema was edited or no saved state — schema pct wins
          panel.dataset.xPct = String(schemaXPct);
          panel.dataset.yPct = String(schemaYPct);
          panel.dataset.placed = 'true';
          panel.dataset.x = String(schemaXPct / 100 * sw);
          panel.dataset.y = String(schemaYPct / 100 * sw);
        } else if (state.placed && state.xPct != null) {
          // Drag-placed position saved as pct — convert to current px
          const xPct = parseFloat(state.xPct) || 0;
          const yPct = parseFloat(state.yPct) || 0;
          panel.dataset.xPct = String(xPct);
          panel.dataset.yPct = String(yPct);
          panel.dataset.placed = 'true';
          panel.dataset.x = String(xPct / 100 * sw);
          panel.dataset.y = String(yPct / 100 * sw);
        } else if (state.placed) {
          // Legacy: saved as px only — keep as-is (backward compat, no resize scaling)
          panel.dataset.xPct = '';
          panel.dataset.yPct = '';
          panel.dataset.placed = 'true';
          panel.dataset.x = String(Number(state.x) || 0);
          panel.dataset.y = String(Number(state.y) || 0);
        } else {
          panel.dataset.xPct = '';
          panel.dataset.yPct = '';
          panel.dataset.placed = 'false';
          panel.dataset.x = '0';
          panel.dataset.y = '0';
        }

        panel.dataset.baseWidth = String(baseWidth);
        panel.dataset.baseHeight = String(resolvedHeight);
        panel.dataset.width = String(baseWidth);
        panel.dataset.height = String(resolvedHeight);
        panel.style.zIndex = String(state.zIndex || index + 1);
      });

      this.normalizeFloatingOrders();
    }

    autoHeightForPanel(panel, panelWidth) {
      const ratioStr = (panel.dataset.imageRatio || '1').trim();
      let ratio = 1;
      if (ratioStr.includes('/')) {
        const parts = ratioStr.split('/');
        ratio = (Number(parts[0].trim()) / Number(parts[1].trim())) || 1;
      } else {
        ratio = Number(ratioStr) || 1;
      }
      const mediaHeight = Math.round(panelWidth / ratio);
      const content = panel.querySelector('.free-panel__content');
      const contentHeight = content ? content.scrollHeight : 0;
      return 44 + mediaHeight + contentHeight + 16;
    }

    bindPanels() {
      this.panels.forEach((panel) => {
        const header = panel.querySelector('[data-free-panel-drag-handle]');

        if (!header) {
          return;
        }

        if (this.pointerSupport) {
          header.addEventListener('pointerdown', (event) => this.startPointerDrag(event, panel));
          return;
        }

        header.addEventListener('mousedown', (event) => this.startMouseDrag(event, panel));
        header.addEventListener('touchstart', (event) => this.startTouchDrag(event, panel), { passive: false });
      });
    }

    startPointerDrag(event, panel) {
      if (this.settings.enableDrag === false) return;
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      this.beginDrag(panel, event.clientX, event.clientY, event.pointerId);
      event.currentTarget.setPointerCapture(event.pointerId);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      event.preventDefault();
    }

    startMouseDrag(event, panel) {
      if (this.settings.enableDrag === false) return;
      if (event.button !== 0 || Date.now() - this.lastTouchTime < 500) {
        return;
      }

      this.beginDrag(panel, event.clientX, event.clientY, 'mouse');
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
      event.preventDefault();
    }

    startTouchDrag(event, panel) {
      if (this.settings.enableDrag === false) return;
      const touch = event.changedTouches[0];
      if (!touch) {
        return;
      }

      this.lastTouchTime = Date.now();
      this.beginDrag(panel, touch.clientX, touch.clientY, 'touch');
      window.addEventListener('touchmove', this.onTouchMove, { passive: false });
      window.addEventListener('touchend', this.onTouchEnd);
      window.addEventListener('touchcancel', this.onTouchEnd);
      event.preventDefault();
    }

    beginDrag(panel, clientX, clientY, pointerId) {
      this.promotePanel(panel);
      const state = this.panelState(panel);
      this.dragState = {
        panel,
        pointerId,
        startX: clientX,
        startY: clientY,
        originX: state.x,
        originY: state.y,
        width: state.width,
        height: state.height,
        previewRect: state,
      };

      panel.classList.add('is-dragging');
      this.clearDockHighlights();
    }

    onPointerMove(event) {
      if (this.dragState && event.pointerId === this.dragState.pointerId) {
        this.handleDragMove(event.clientX, event.clientY);
      }
    }

    onPointerUp(event) {
      if (this.dragState && event.pointerId === this.dragState.pointerId) {
        this.finishDrag();
      }
      this.removeGlobalListeners();
    }

    onMouseMove(event) {
      if (this.dragState) {
        this.handleDragMove(event.clientX, event.clientY);
      }
    }

    onMouseUp() {
      if (this.dragState) {
        this.finishDrag();
      }
      this.removeGlobalListeners();
    }

    onTouchMove(event) {
      const touch = event.changedTouches[0];
      if (!touch) {
        return;
      }

      if (this.dragState) {
        this.handleDragMove(touch.clientX, touch.clientY);
      }

      event.preventDefault();
    }

    onTouchEnd() {
      if (this.dragState) {
        this.finishDrag();
      }
      this.removeGlobalListeners();
    }

    handleDragMove(clientX, clientY) {
      const dragState = this.dragState;
      if (!dragState) {
        return;
      }

      const nextX = dragState.originX + (clientX - dragState.startX);
      const nextY = dragState.originY + (clientY - dragState.startY);
      dragState.lastClientX = clientX;
      dragState.lastClientY = clientY;

      this.applyPanelRect(dragState.panel, {
        x: nextX,
        y: nextY,
        width: dragState.width,
        height: dragState.height,
      });

      const dockMatch = this.detectDockCollision(clientX, clientY);
      this.highlightDock(dockMatch ? dockMatch.zone : null);

      if (dockMatch) {
        this.hideDropIndicator();
        return;
      }

      const preview = this.updateDropPreview(dragState.panel, {
        x: nextX,
        y: nextY,
        width: dragState.width,
        height: dragState.height,
      });
      dragState.previewRect = preview.rect;
    }

    finishDrag() {
      const dragState = this.dragState;
      if (!dragState) {
        return;
      }

      dragState.panel.classList.remove('is-dragging');
      const dockMatch = this.detectDockCollision(dragState.lastClientX, dragState.lastClientY);

      if (dockMatch) {
        dragState.panel.dataset.dock = dockMatch.zone;
        dragState.panel.dataset.dockOrder = String(this.nextDockOrder(dockMatch.zone));
        dragState.panel.dataset.placed = 'false';
      } else {
        dragState.panel.dataset.dock = 'float';
        dragState.panel.dataset.placed = 'true';
        if (dragState.previewRect) {
          this.applyPanelRect(dragState.panel, dragState.previewRect);
          // Save position as percentage for responsive scaling on resize
          const sw = this.clientWidth || 1;
          dragState.panel.dataset.xPct = String((dragState.previewRect.x / sw) * 100);
          dragState.panel.dataset.yPct = String((dragState.previewRect.y / sw) * 100);
        }
      }

      // Clear schema pct anchors — position is now drag-based
      // (schemaXPct/schemaYPct fields are legacy, xPct/yPct is the canonical store now)

      this.hideDropIndicator();
      this.relayout();
      this.clearDockHighlights();
      this.updateCoordReadout(dragState.panel);
      this.persistState();
      this.renderStatus();
      this.dragState = null;
    }



    relayout() {
      if (this.isMobile()) {
        this._wasMobile = true;
        this.mobileLayout();
      } else {
        if (this._wasMobile) {
          // Transitioning back from mobile to desktop: reset positions from localStorage/schema
          this._wasMobile = false;
          this.destroyScrollNav();
          this.classList.remove('free-panel__stage--mobile-scroll');
          if (this.parentElement) this.parentElement.classList.remove('free-panel__stage-wrap--mobile-scroll');
          this.style.width = '';
          this.restoreState();
        } else {
          this.destroyScrollNav();
          this.classList.remove('free-panel__stage--mobile-scroll');
          if (this.parentElement) this.parentElement.classList.remove('free-panel__stage-wrap--mobile-scroll');
          this.style.width = '';
        }
        this._applySchemaPercentagePositions();
        this.layoutDockedPanels();
        this.layoutFloatingPanels();
        if (this.hasOverlaps()) {
          this.resolvePanelCollisions();
        }
      }
      this.updateStageHeight();
    }

    isMobile() {
      return window.innerWidth < 990;
    }

    mobileLayout() {
      const scrollEnabled = Boolean(this.settings.mobileScrollEnabled);
      const cols = Math.max(1, parseInt(this.settings.mobileGridColumns, 10) || 2);
      const gapX = this.gapX();
      const gapY = this.gapY();
      const wrapper = this.parentElement;

      // Reset any previously set width so clientWidth reflects the natural container width
      this.style.width = '';
      const stageWidth = this.parentElement ? this.parentElement.clientWidth : this.clientWidth;

      // All panels undocked for mobile grid
      this.panels.forEach((panel) => panel.classList.remove('is-docked'));

      if (scrollEnabled) {
        // Single-row horizontal scroll: panels are 80% viewport width each
        const panelWidth = Math.max(this.minWidth(), Math.round(stageWidth * 0.8));
        const totalWidth = this.panels.length * (panelWidth + gapX) + gapX;

        this.classList.add('free-panel__stage--mobile-scroll');
        if (wrapper) wrapper.classList.add('free-panel__stage-wrap--mobile-scroll');
        this.style.width = totalWidth + 'px';

        this.panels.forEach((panel, i) => {
          const panelHeight = this.autoHeightForPanel(panel, panelWidth);
          this.applyPanelRect(panel, {
            x: gapX + i * (panelWidth + gapX),
            y: gapY,
            width: panelWidth,
            height: panelHeight,
          });
        });

        if (wrapper) this.setupScrollNav(wrapper, panelWidth, gapX);
      } else {
        this.destroyScrollNav();
        // Multi-column grid layout
        this.classList.remove('free-panel__stage--mobile-scroll');
        if (wrapper) wrapper.classList.remove('free-panel__stage-wrap--mobile-scroll');
        this.style.width = '';

        const panelWidth = Math.max(
          this.minWidth(),
          Math.floor((stageWidth - gapX * (cols + 1)) / cols)
        );

        let colIndex = 0;
        let rowY = gapY;
        let rowMaxHeight = 0;

        this.panels.forEach((panel) => {
          if (colIndex >= cols) {
            colIndex = 0;
            rowY += rowMaxHeight + gapY;
            rowMaxHeight = 0;
          }
          const x = gapX + colIndex * (panelWidth + gapX);
          const panelHeight = this.autoHeightForPanel(panel, panelWidth);
          this.applyPanelRect(panel, { x, y: rowY, width: panelWidth, height: panelHeight });
          rowMaxHeight = Math.max(rowMaxHeight, panelHeight);
          colIndex += 1;
        });
      }
    }

    setupScrollNav(wrapper, panelWidth, gapX) {
      if (!this._scrollNav) {
        const nav = document.createElement('div');
        nav.className = 'free-panel__scroll-nav';
        nav.innerHTML =
          '<button class="free-panel__scroll-btn free-panel__scroll-btn--prev" aria-label="上一个">&#8249;</button>' +
          '<span class="free-panel__scroll-indicator"></span>' +
          '<button class="free-panel__scroll-btn free-panel__scroll-btn--next" aria-label="下一个">&#8250;</button>';
        if (wrapper.parentElement) {
          wrapper.parentElement.insertBefore(nav, wrapper.nextSibling);
        }
        this._scrollNav = nav;
        this._scrollNavIndex = 0;
        nav.querySelector('.free-panel__scroll-btn--prev').addEventListener('click', () => {
          this._scrollNavIndex = Math.max(0, (this._scrollNavIndex || 0) - 1);
          this._scrollToIndex(wrapper);
          this._updateScrollNavUI();
        });
        nav.querySelector('.free-panel__scroll-btn--next').addEventListener('click', () => {
          this._scrollNavIndex = Math.min(this.panels.length - 1, (this._scrollNavIndex || 0) + 1);
          this._scrollToIndex(wrapper);
          this._updateScrollNavUI();
        });
        this._onScrollNavScroll = () => {
          const step = (this._scrollNavPanelWidth || 1) + (this._scrollNavGapX || 0);
          this._scrollNavIndex = Math.round(wrapper.scrollLeft / step);
          this._updateScrollNavUI();
        };
        wrapper.addEventListener('scroll', this._onScrollNavScroll, { passive: true });
        this._scrollNavWrapper = wrapper;
      }
      this._scrollNavPanelWidth = panelWidth;
      this._scrollNavGapX = gapX;
      this._updateScrollNavUI();
    }

    _scrollToIndex(wrapper) {
      const step = (this._scrollNavPanelWidth || 0) + (this._scrollNavGapX || 0);
      if (wrapper && step > 0) {
        wrapper.scrollTo({ left: this._scrollNavIndex * step, behavior: 'smooth' });
      }
    }

    _updateScrollNavUI() {
      if (!this._scrollNav) return;
      const total = this.panels.length;
      const current = Math.min(Math.max((this._scrollNavIndex || 0) + 1, 1), total);
      this._scrollNav.querySelector('.free-panel__scroll-indicator').textContent = current + ' / ' + total;
      this._scrollNav.querySelector('.free-panel__scroll-btn--prev').disabled = current <= 1;
      this._scrollNav.querySelector('.free-panel__scroll-btn--next').disabled = current >= total;
    }

    destroyScrollNav() {
      if (!this._scrollNav) return;
      if (this._scrollNavWrapper && this._onScrollNavScroll) {
        this._scrollNavWrapper.removeEventListener('scroll', this._onScrollNavScroll);
      }
      if (this._scrollNav.parentElement) {
        this._scrollNav.parentElement.removeChild(this._scrollNav);
      }
      this._scrollNav = null;
      this._scrollNavWrapper = null;
      this._onScrollNavScroll = null;
    }

    _applySchemaPercentagePositions() {
      const sw = this.clientWidth || 1;
      this.panels.forEach((panel) => {
        if (panel.dataset.dock !== 'float') return;
        // Both schema-based and drag-based positions are stored as xPct/yPct.
        // Re-derive px from pct on every relayout so resize always adapts.
        const xPct = parseFloat(panel.dataset.xPct || '');
        const yPct = parseFloat(panel.dataset.yPct || '');
        if (isNaN(xPct) || isNaN(yPct)) return;
        panel.dataset.x = String(xPct / 100 * sw);
        panel.dataset.y = String(yPct / 100 * sw);
        panel.dataset.placed = 'true';
      });
    }

    layoutDockedPanels() {
      const dockedZones = ['top', 'right', 'bottom', 'left'];

      dockedZones.forEach((zone) => {
        const panels = this.panels
          .filter((panel) => panel.dataset.dock === zone)
          .sort((first, second) => Number(first.dataset.dockOrder) - Number(second.dataset.dockOrder));

        if (!panels.length) {
          return;
        }

        const placement = this.flowPanels(panels, zone, true);

        panels.forEach((panel, index) => {
          const rect = placement[index];
          panel.classList.add('is-docked');
          this.applyPanelRect(panel, rect);
        });
      });

      this.panels
        .filter((panel) => panel.dataset.dock === 'float')
        .forEach((panel) => panel.classList.remove('is-docked'));
    }

    layoutFloatingPanels() {
      const floatPanels = this.panels
        .filter((panel) => panel.dataset.dock === 'float')
        .sort((first, second) => Number(first.dataset.order) - Number(second.dataset.order));

      if (!floatPanels.length) {
        return;
      }

      const placedRects = [];
      const autoCursor = {
        x: this.floatBounds().minX,
        y: this.floatBounds().minY,
      };
      let currentRowBottom = autoCursor.y;

      floatPanels.forEach((panel) => {
        const width = this.itemWidth(panel);
        const height = this.itemHeight(panel);
        const preferred = panel.dataset.placed === 'true'
          ? { x: Number(panel.dataset.x) || autoCursor.x, y: Number(panel.dataset.y) || autoCursor.y }
          : { x: autoCursor.x, y: autoCursor.y };
        const preferredRect = { x: preferred.x, y: preferred.y, width, height };
        const rect = panel.dataset.placed === 'true' && this.isRectUsable(preferredRect, placedRects)
          ? this.normalizeRect(preferredRect)
          : this.findNearestFreeRect({ width, height }, preferred, panel, placedRects);
        this.applyPanelRect(panel, rect);
        placedRects.push(rect);
        if (panel.dataset.placed !== 'true') {
          panel.dataset.placed = 'true';
        }

        const nextX = rect.x + rect.width + this.gapX();
        const bounds = this.floatBounds();
        currentRowBottom = Math.max(currentRowBottom, rect.y + rect.height);
        if (nextX + width <= bounds.maxX) {
          autoCursor.x = nextX;
          autoCursor.y = rect.y;
        } else {
          autoCursor.x = bounds.minX;
          autoCursor.y = currentRowBottom + this.gapY();
          currentRowBottom = autoCursor.y;
        }
      });
    }

    updateDropPreview(draggedPanel, draggedRect) {
      const placedRects = this.panels
        .filter((panel) => panel !== draggedPanel)
        .map((panel) => this.panelState(panel));
      const rect = this.findNearestFreeRect(
        { width: draggedRect.width, height: draggedRect.height },
        { x: draggedRect.x, y: draggedRect.y },
        draggedPanel,
        placedRects
      );

      this.showDropIndicator(rect);
      return { rect };
    }

    updateStageHeight() {
      const gapY = this.gapY();
      const lowestPoint = this.panels.reduce((max, panel) => {
        const state = this.panelState(panel);
        return Math.max(max, state.y + state.height);
      }, 0);
      const statusHeight = this.status ? this.status.offsetHeight : 0;
      // In mobile scroll mode don't enforce a minimum stage height — fit content exactly
      const isMobileScroll = this.isMobile() && Boolean(this.settings.mobileScrollEnabled);
      const minHeight = isMobileScroll
        ? 0
        : (Number(this.settings.stageMinHeight) || parseFloat(getComputedStyle(this).minHeight) || 0);
      const nextHeight = Math.max(minHeight, lowestPoint + gapY + statusHeight + (isMobileScroll ? 0 : 24));

      this.style.height = nextHeight + 'px';
    }

    getDockInsets() {
      const dockSize = this.dockSize();
      return {
        top: this.hasDockedPanels('top') ? dockSize : 0,
        right: this.hasDockedPanels('right') ? dockSize : 0,
        bottom: this.hasDockedPanels('bottom') ? dockSize : 0,
        left: this.hasDockedPanels('left') ? dockSize : 0,
      };
    }

    hasDockedPanels(zone) {
      return this.panels.some((panel) => panel.dataset.dock === zone);
    }

    flowPanels(panels, zone, isDocked) {
      return this.flowLayout(panels, zone, isDocked);
    }

    flowLayout(items, zone, isDocked) {
      const gapX = this.gapX();
      const gapY = this.gapY();
      const stageRect = this.stageRect();
      const dockInsets = this.getDockInsets();
      const startX = isDocked
        ? zone === 'left'
          ? gapX
          : zone === 'right'
            ? Math.max(gapX, stageRect.width - this.dockSize() + gapX)
            : gapX
        : dockInsets.left + gapX;
      const limitX = isDocked
        ? zone === 'left' || zone === 'right'
          ? Math.min(stageRect.width - gapX, startX + this.dockSize() - gapX * 2)
          : stageRect.width - gapX
        : stageRect.width - dockInsets.right - gapX;
      let cursorX = startX;
      let cursorY = isDocked
        ? zone === 'bottom'
          ? Math.max(gapY, stageRect.height - this.dockSize() + gapY)
          : gapY
        : dockInsets.top + gapY;
      let rowHeight = 0;
      const layout = [];

      items.forEach((item) => {
        const desiredWidth = this.itemWidth(item);
        const desiredHeight = this.itemHeight(item);
        const maxWidth = Math.max(this.minWidth(), limitX - startX);
        const width = this.clamp(desiredWidth, this.minWidth(), maxWidth);
        const height = Math.max(this.minHeight(), desiredHeight);

        if (cursorX > startX && cursorX + width > limitX) {
          cursorX = startX;
          cursorY += rowHeight + gapY;
          rowHeight = 0;
        }

        let x = cursorX;
        let y = cursorY;

        if (isDocked && zone === 'right') {
          x = stageRect.width - width - gapX;
        }

        if (isDocked && zone === 'bottom') {
          y = stageRect.height - height - gapY;
        }

        layout.push({ x, y, width, height });

        cursorX += width + gapX;
        rowHeight = Math.max(rowHeight, height);
      });

      return layout;
    }

    itemWidth(item) {
      if (item && item.__preview) {
        return Number(item.width) || this.settings.defaultWidth || 320;
      }
      return Number(item.dataset.baseWidth) || Number(item.dataset.width) || this.settings.defaultWidth || 320;
    }

    itemHeight(item) {
      if (item && item.__preview) {
        return Number(item.height) || this.settings.defaultHeight || 280;
      }
      return Math.max(
        Number(item.dataset.baseHeight) || Number(item.dataset.height) || this.settings.defaultHeight || 280,
        this.panelMinimumHeight(item)
      );
    }

    panelMinimumHeight(panel) {
      const header = panel.querySelector('.free-panel__panel-header');
      const content = panel.querySelector('.free-panel__content');
      const headerHeight = header ? header.offsetHeight : 52;
      const contentHeight = content ? content.scrollHeight : 0;
      const mediaHeight = this.panelMinimumMediaHeight();

      return Math.ceil(headerHeight + contentHeight + mediaHeight + 8);
    }

    panelMinimumMediaHeight() {
      return window.innerWidth < 990 ? 72 : 80;
    }

    resolvePanelCollisions() {
      const gapX = this.gapX();
      const gapY = this.gapY();
      const stageRect = this.stageRect();
      const panels = this.panels
        .slice()
        .sort((first, second) => {
          const firstDockRank = this.dockRank(first.dataset.dock);
          const secondDockRank = this.dockRank(second.dataset.dock);

          if (firstDockRank !== secondDockRank) {
            return firstDockRank - secondDockRank;
          }

          if (first.dataset.dock === 'float' && second.dataset.dock === 'float') {
            return Number(first.dataset.order) - Number(second.dataset.order);
          }

          return Number(first.dataset.dockOrder) - Number(second.dataset.dockOrder);
        });

      const placed = [];

      panels.forEach((panel) => {
        const desired = this.panelState(panel);
        const width = this.clamp(desired.width, this.minWidth(), Math.max(this.minWidth(), stageRect.width - gapX * 2));
        const x = this.clamp(desired.x, gapX, Math.max(gapX, stageRect.width - width - gapX));
        let rect = {
          x,
          y: Math.max(gapY, desired.y),
          width,
          height: Math.max(this.minHeight(), desired.height),
        };

        let guard = 0;
        while (guard < 200) {
          const overlapping = placed.filter((placedRect) => this.rectanglesOverlap(rect, placedRect, gapX, gapY));
          if (!overlapping.length) {
            break;
          }

          rect.y = Math.max.apply(
            null,
            overlapping.map((placedRect) => placedRect.y + placedRect.height + gapY)
          );
          guard += 1;
        }

        this.applyPanelRect(panel, rect);
        placed.push(rect);
      });
    }

    findNearestFreeRect(size, preferred, excludedPanel, existingRects = []) {
      const gapX = this.gapX();
      const gapY = this.gapY();
      const bounds = this.floatBounds();
      const width = this.clamp(size.width, this.minWidth(), Math.max(this.minWidth(), bounds.maxX - bounds.minX));
      const height = Math.max(this.minHeight(), size.height);
      const stepX = Math.max(8, gapX || 8);
      const stepY = Math.max(8, gapY || 8);
      const snappedPreferred = {
        x: this.snapToAxis(preferred.x, bounds.minX, stepX),
        y: this.snapToAxis(preferred.y, bounds.minY, stepY),
      };
      const maxY = Math.max(bounds.minY, this.maxOccupiedY(existingRects) + gapY + height + this.settings.stageMinHeight / 4);

      // Build candidate Y set: grid steps + edge-aligned positions from existing panels
      const candidateYSet = new Set();
      for (let y = bounds.minY; y <= maxY; y += stepY) {
        candidateYSet.add(y);
      }
      existingRects.forEach((r) => {
        const topSnap = bounds.minY + Math.round((r.y - bounds.minY) / stepY) * stepY;
        const bottomSnap = bounds.minY + Math.round((r.y + r.height + gapY - bounds.minY) / stepY) * stepY;
        if (r.y >= bounds.minY && r.y <= maxY) candidateYSet.add(r.y);
        if (topSnap >= bounds.minY && topSnap <= maxY) candidateYSet.add(topSnap);
        const afterBottom = r.y + r.height + gapY;
        if (afterBottom >= bounds.minY && afterBottom <= maxY) candidateYSet.add(afterBottom);
        if (bottomSnap >= bounds.minY && bottomSnap <= maxY) candidateYSet.add(bottomSnap);
      });
      const candidateYs = Array.from(candidateYSet).sort((a, b) => a - b);

      // Build candidate X set similarly
      const candidateXSet = new Set();
      for (let x = bounds.minX; x <= Math.max(bounds.minX, bounds.maxX - width); x += stepX) {
        candidateXSet.add(x);
      }
      existingRects.forEach((r) => {
        const afterRight = r.x + r.width + gapX;
        if (r.x >= bounds.minX && r.x <= bounds.maxX - width) candidateXSet.add(r.x);
        if (afterRight >= bounds.minX && afterRight <= bounds.maxX - width) candidateXSet.add(afterRight);
      });
      const candidateXs = Array.from(candidateXSet).sort((a, b) => a - b);

      let bestRect = null;
      let bestScore = Number.POSITIVE_INFINITY;

      for (const y of candidateYs) {
        for (const x of candidateXs) {
          const rect = { x, y, width, height };
          if (existingRects.some((placedRect) => this.rectanglesOverlap(rect, placedRect, gapX, gapY))) {
            continue;
          }

          const score = Math.hypot(snappedPreferred.x - x, snappedPreferred.y - y);
          if (score < bestScore) {
            bestRect = rect;
            bestScore = score;
          }
        }
      }

      if (bestRect) {
        return bestRect;
      }

      return {
        x: bounds.minX,
        y: maxY,
        width,
        height,
      };
    }

    normalizeRect(rect) {
      const bounds = this.floatBounds();
      return {
        x: this.clamp(rect.x, bounds.minX, Math.max(bounds.minX, bounds.maxX - rect.width)),
        y: Math.max(bounds.minY, rect.y),
        width: rect.width,
        height: rect.height,
      };
    }

    isRectUsable(rect, existingRects) {
      const normalized = this.normalizeRect(rect);
      return !existingRects.some((placedRect) => this.rectanglesOverlap(normalized, placedRect, this.gapX(), this.gapY()));
    }

    floatBounds() {
      const gapX = this.gapX();
      const gapY = this.gapY();
      const stageRect = this.stageRect();
      const insets = this.getDockInsets();
      return {
        minX: insets.left + gapX,
        minY: insets.top + gapY,
        maxX: stageRect.width - insets.right - gapX,
      };
    }

    maxOccupiedY(rects) {
      return rects.reduce((max, rect) => Math.max(max, rect.y + rect.height), 0);
    }

    hasOverlaps() {
      const gapX = this.gapX();
      const gapY = this.gapY();

      for (let index = 0; index < this.panels.length; index += 1) {
        const first = this.panelState(this.panels[index]);
        for (let otherIndex = index + 1; otherIndex < this.panels.length; otherIndex += 1) {
          const second = this.panelState(this.panels[otherIndex]);
          if (this.rectanglesOverlap(first, second, gapX, gapY)) {
            return true;
          }
        }
      }

      return false;
    }

    rectanglesOverlap(first, second, gapX, gapY) {
      return !(
        first.x + first.width + gapX <= second.x ||
        second.x + second.width + gapX <= first.x ||
        first.y + first.height + gapY <= second.y ||
        second.y + second.height + gapY <= first.y
      );
    }

    dockRank(dock) {
      if (dock === 'top') {
        return 0;
      }
      if (dock === 'left') {
        return 1;
      }
      if (dock === 'right') {
        return 2;
      }
      if (dock === 'bottom') {
        return 3;
      }
      return 4;
    }

    detectDockCollision(clientX, clientY) {
      if (clientX == null || clientY == null) return null;
      const stageBounds = this.getBoundingClientRect();
      const relX = clientX - stageBounds.left;
      const relY = clientY - stageBounds.top;
      const stageRect = this.stageRect();
      const dockSize = this.dockSize();

      if (relY >= 0 && relY < dockSize) return { zone: 'top' };
      if (relY > stageRect.height - dockSize && relY <= stageRect.height) return { zone: 'bottom' };
      if (relX >= 0 && relX < dockSize) return { zone: 'left' };
      if (relX > stageRect.width - dockSize && relX <= stageRect.width) return { zone: 'right' };
      return null;
    }

    normalizeFloatingOrders() {
      this.panels
        .filter((panel) => panel.dataset.dock === 'float')
        .sort((first, second) => Number(first.dataset.order) - Number(second.dataset.order))
        .forEach((panel, index) => {
          panel.dataset.order = String(index);
        });
    }

    relativeZoneRect(zone) {
      const stageRect = this.stageRect();
      const dockSize = this.dockSize();

      if (zone === 'top') {
        return { left: 0, top: 0, right: stageRect.width, bottom: dockSize };
      }
      if (zone === 'right') {
        return { left: stageRect.width - dockSize, top: 0, right: stageRect.width, bottom: stageRect.height };
      }
      if (zone === 'bottom') {
        return { left: 0, top: stageRect.height - dockSize, right: stageRect.width, bottom: stageRect.height };
      }
      return { left: 0, top: 0, right: dockSize, bottom: stageRect.height };
    }

    highlightDock(zone) {
      this.zones.forEach((zoneEl) => {
        zoneEl.classList.toggle('is-active', Boolean(zone) && zoneEl.dataset.dockZone === zone);
      });
    }

    showDropIndicator(rect) {
      this.dropIndicator.style.left = rect.x + 'px';
      this.dropIndicator.style.top = rect.y + 'px';
      this.dropIndicator.style.width = rect.width + 'px';
      this.dropIndicator.style.height = rect.height + 'px';
      this.dropIndicator.style.display = 'flex';
      this.dropIndicator.classList.add('is-visible');
    }

    hideDropIndicator() {
      this.dropIndicator.style.display = 'none';
      this.dropIndicator.classList.remove('is-visible');
    }

    clearDockHighlights() {
      this.highlightDock(null);
    }

    stageRect() {
      return {
        width: this.clientWidth,
        height: this.clientHeight,
      };
    }

    applyPanelRect(panel, rect) {
      panel.style.left = rect.x + 'px';
      panel.style.top = rect.y + 'px';
      panel.style.width = rect.width + 'px';
      panel.style.height = rect.height + 'px';
      panel.dataset.x = String(rect.x);
      panel.dataset.y = String(rect.y);
      panel.dataset.width = String(rect.width);
      panel.dataset.height = String(rect.height);
    }

    panelState(panel) {
      return {
        x: Number(panel.dataset.x) || 0,
        y: Number(panel.dataset.y) || 0,
        width: Number(panel.dataset.width) || Number(panel.dataset.baseWidth) || this.settings.defaultWidth || 320,
        height: Number(panel.dataset.height) || Number(panel.dataset.baseHeight) || this.settings.defaultHeight || 280,
      };
    }

    nextDockOrder(zone) {
      const orders = this.panels
        .filter((panel) => panel.dataset.dock === zone)
        .map((panel) => Number(panel.dataset.dockOrder) || 0);
      return orders.length ? Math.max.apply(null, orders) + 1 : 0;
    }

    promotePanel(panel) {
      const highestZ = this.panels.reduce((current, item) => Math.max(current, Number(item.style.zIndex) || 1), 1);
      panel.style.zIndex = String(highestZ + 1);
    }

    persistState() {
      if (!this.stateKey) {
        return;
      }

      const payload = {};
      const sw = this.clientWidth || 1;
      this.panels.forEach((panel, index) => {
        const sXPct = parseFloat((panel.dataset.schemaX || '').trim());
        const sYPct = parseFloat((panel.dataset.schemaY || '').trim());
        // Prefer stored xPct/yPct; fall back to computing from current px
        const xPct = panel.dataset.xPct !== '' && panel.dataset.xPct != null
          ? parseFloat(panel.dataset.xPct)
          : (Number(panel.dataset.x) || 0) / sw * 100;
        const yPct = panel.dataset.yPct !== '' && panel.dataset.yPct != null
          ? parseFloat(panel.dataset.yPct)
          : (Number(panel.dataset.y) || 0) / sw * 100;
        payload[panel.dataset.panelId] = {
          x: Number(panel.dataset.x) || 0,
          y: Number(panel.dataset.y) || 0,
          xPct: isNaN(xPct) ? 0 : xPct,
          yPct: isNaN(yPct) ? 0 : yPct,
          width: Number(panel.dataset.width) || 0,
          height: Number(panel.dataset.height) || 0,
          baseWidth: Number(panel.dataset.baseWidth) || Number(panel.dataset.width) || 0,
          baseHeight: Number(panel.dataset.baseHeight) || Number(panel.dataset.height) || 0,
          userResizedWidth: panel.dataset.userResizedWidth === 'true',
          userResizedHeight: panel.dataset.userResizedHeight === 'true',
          dock: panel.dataset.dock || 'float',
          placed: panel.dataset.placed === 'true',
          order: Number(panel.dataset.order) || 0,
          dockOrder: Number(panel.dataset.dockOrder) || index,
          zIndex: Number(panel.style.zIndex) || index + 1,
          lastSchemaXPct: isNaN(sXPct) ? '' : String(sXPct),
          lastSchemaYPct: isNaN(sYPct) ? '' : String(sYPct),
        };
      });

      window.localStorage.setItem(this.stateKey, JSON.stringify(payload));
    }

    loadStoredState() {
      if (!this.stateKey) {
        return {};
      }

      try {
        return JSON.parse(window.localStorage.getItem(this.stateKey)) || {};
      } catch (error) {
        return {};
      }
    }

    updateCoordReadout(panel) {
      const readout = panel.querySelector('[data-free-panel-coord]');
      if (!readout) {
        return;
      }

      // Prefer xPct (canonical stored value) so the readout is stable across resize.
      // Fall back to computing from actual rendered x when xPct is not set.
      const xPctStored = parseFloat(panel.dataset.xPct || '');
      const yPctStored = parseFloat(panel.dataset.yPct || '');
      let xDisp, yDisp;
      if (!isNaN(xPctStored) && !isNaN(yPctStored)) {
        xDisp = xPctStored.toFixed(1);
        yDisp = yPctStored.toFixed(1);
      } else {
        const sw = this.clientWidth || 1;
        xDisp = ((Number(panel.dataset.x) || 0) / sw * 100).toFixed(1);
        yDisp = ((Number(panel.dataset.y) || 0) / sw * 100).toFixed(1);
      }
      readout.textContent = 'X: ' + xDisp + '%  Y: ' + yDisp + '%';
    }

    renderStatus() {
      if (!this.status) {
        return;
      }

      const floating = this.panels.filter((panel) => panel.dataset.dock === 'float').length;
      const docked = this.panels.length - floating;
      const labels = [
        '自动排布 ' + floating + ' 个窗格',
        '停靠窗格 ' + docked,
        window.innerWidth < 990 ? '触摸拖拽' : '鼠标拖拽',
      ];

      this.status.innerHTML = labels.map((label) => '<span class="free-panel__pill">' + label + '</span>').join('');
    }

    onWindowResize() {
      this.relayout();
      // Do NOT call persistState() here — resizing should never overwrite stored xPct
      // values with position data computed at a non-standard viewport width.
      // Positions are persisted only after explicit user actions (drag).
      this.renderStatus();
      this.panels.forEach((panel) => this.updateCoordReadout(panel));
    }

    gap() {
      return this.settings.gap || 16;
    }

    gapX() {
      return this.settings.horizontalGap == null ? this.gap() : this.settings.horizontalGap;
    }

    gapY() {
      return this.settings.verticalGap == null ? this.gap() : this.settings.verticalGap;
    }

    dockSize() {
      return this.settings.dockZoneSize || (window.innerWidth < 990 ? 72 : 88);
    }

    snapToAxis(value, origin, step) {
      const normalized = value - origin;
      return origin + Math.round(normalized / step) * step;
    }

    minWidth() {
      return window.innerWidth < 990 ? (this.settings.mobileMinWidth || 180) : (this.settings.minWidth || 220);
    }

    minHeight() {
      return window.innerWidth < 990 ? (this.settings.mobileMinHeight || 150) : (this.settings.minHeight || 180);
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    removeGlobalListeners() {
      this.hideDropIndicator();
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      window.removeEventListener('touchmove', this.onTouchMove);
      window.removeEventListener('touchend', this.onTouchEnd);
      window.removeEventListener('touchcancel', this.onTouchEnd);
    }
  }

  customElements.define('free-panel-board', FreePanelBoard);
}